import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Check, X, Ban, RotateCcw, Search } from "lucide-react";

export default function AdminUsers() {
  const t = useT();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");

  const q = useQuery({
    queryKey: ["admin-access-users"],
    queryFn: async () => (await api.get("/admin/access/users")).data,
    retry: false,
  });

  const users = useMemo(() => {
    const raw = Array.isArray(q.data) ? q.data : q.data?.users || q.data?.items || [];
    if (!query) return raw;
    const s = query.toLowerCase();
    return raw.filter((u) =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.status || "").toLowerCase().includes(s)
    );
  }, [q.data, query]);

  const act = async (path, user_id, verb) => {
    try {
      await api.post(path);
      toast.success(`${verb}`);
      qc.invalidateQueries({ queryKey: ["admin-access-users"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.admin")} title={t("nav.users")} subtitle={t("admin.subtitle")} />

      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-4 w-4 text-ink-500" strokeWidth={1.75} />
          <Input
            data-testid="admin-users-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("common.search")}
            className="rounded-xl bg-cream-50 h-11 max-w-md"
          />
        </div>
        {q.isLoading && <LoadingCard height="h-40" />}
        {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
        {!q.isLoading && !q.isError && users.length === 0 && (
          <EmptyState icon={Users} title={t("common.empty")} testId="admin-users-empty" />
        )}
        <div className="overflow-x-auto" data-testid="admin-users-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 text-[11px] uppercase tracking-[0.15em]">
                <th className="py-3 px-3">{t("common.name")}</th>
                <th className="py-3 px-3">{t("common.email")}</th>
                <th className="py-3 px-3">{t("common.status")}</th>
                <th className="py-3 px-3">{t("common.role")}</th>
                <th className="py-3 px-3 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const id = u.id || u._id || u.user_id;
                const st = (u.status || "").toLowerCase();
                return (
                  <tr key={id || i} className="border-t border-border hover:bg-cream-50" data-testid={`admin-user-row-${i}`}>
                    <td className="py-3 px-3 text-ink-900">{u.name || "—"}</td>
                    <td className="py-3 px-3 text-ink-700">{u.email || "—"}</td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                        st === "approved" ? "bg-sage-100 text-sage-700"
                        : st === "pending" ? "bg-clay-500/10 text-clay-600"
                        : st === "revoked" ? "bg-rust-500/10 text-rust-600"
                        : "bg-cream-100 text-ink-500"
                      }`}>{u.status || "—"}</span>
                    </td>
                    <td className="py-3 px-3 text-ink-700">
                      {Array.isArray(u.roles) ? u.roles.join(", ") : (u.role || "—")}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="outline" className="rounded-full h-8 border-border" data-testid={`admin-user-approve-${i}`}
                          onClick={() => act(`/admin/access/users/${id}/approve`, id, "approved")}>
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-8 border-border" data-testid={`admin-user-reject-${i}`}
                          onClick={() => act(`/admin/access/users/${id}/reject`, id, "rejected")}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-8 border-border text-rust-600" data-testid={`admin-user-revoke-${i}`}
                          onClick={() => act(`/admin/access/users/${id}/revoke`, id, "revoked")}>
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-8 border-border" data-testid={`admin-user-restore-${i}`}
                          onClick={() => act(`/admin/access/users/${id}/restore`, id, "restored")}>
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
